import React, { useState, useRef, useEffect } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams } from "react-router-dom";
import { addressLine1Validation, cityValidation, emailValidation, firstNameValidation, lastNameValidation, statusValidation, passwordValidation, phone2Validation, phoneValidation, stateValidation, zipcodeValidation } from "validationSchemas/common";
import { getAuthCookie } from "../../utils/authCookies";

const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

interface AgentDetails {
  brokerageFirmAffiliation: string;
  managingBroker: string;
  agentID: string;
  licenseNumber: string;
  licenseExpiryDate: string;
};

interface FirmInfo {
  companyID: string;
  companyName: string;
  address: Address;
  specialities: string;
  regionsServed: string;
}

interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

interface Agent {
  id: string;
  companyID: string;
  companyName: string;
  licenseType: string;
  status: string;
  billingCycle: string;
  licenseValidity: string;
  firstName: string;
  lastName: string;
  userType: string;
  email: string;
  phone1: string;
  phone2: string;
  firmDetails: FirmInfo;
  modulePermissions: string[];
  created_at: string;
  agentDetails: AgentDetails;
}


interface BrokerageFirm {
  id: string;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone1: string;
  phone2: string;
  statuss: string;
}

type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress: OfficeAddress;
};

type OfficeAddress = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

const SignageBrokerageAddAgent = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const [brokerageFirm, setBrokerageFirm] = useState<BrokerageFirm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const navigate = useNavigate();
  const [offices, setOffices] = useState<Office[]>([]);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [validation, setValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevVisibility) => !prevVisibility);
  };

  const validatePassword = (password: string) => {
    setValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.handleChange(e);
    validatePassword(value);
  };

  const [showValidationBox, setShowValidationBox] = useState(false);

  const handleFocus = () => {
    setShowValidationBox(true);
  };

  const handleBlur = () => {
    setShowValidationBox(false);
  };


  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["places"],
  });

  const [address, setAddress] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch License Data
  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${firmId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch license data");
        }

        const data = await response.json();
        if (data && data.firmDetails && data.firmDetails.address) {
          const fetchedLicense: BrokerageFirm = {
            id: data.id || "",
            companyName: data.firmDetails.companyName || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone1: data.phone1 || "",
            phone2: data.phone2 || "",
            statuss: data.status || "Active",
          };

          setBrokerageFirm(fetchedLicense);

        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching license data:", error);
        setError("Failed to fetch license data.");
      } finally {
        setLoading(false);
      }
    };

    if (!brokerageFirm) {
      fetchLicense();
    }
  }, [firmId, brokerageFirm, token]);

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setOffices(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOffices();
  }, [token]);

  function getOfficeById(officeId: string) {
    console.log("Office ID:", officeId);
    return offices?.find(office => office._id === officeId) || null;
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: "",
      lastName: "",
      phone1: "",
      phone2: "",
      email: "",
      password: "",
      brokerageFirmAffiliation: brokerageFirm?.companyName || "",
      agentID: "",
      licenseNumber: "",
      licenseExpiryDate: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      statuss: "",
      officeId: "",
      cityCode: "",
      stateCode: "",
      countryCode: "",
      latitude: 0,
      longitude: 0,
    },
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      phone2: phone2Validation,
      email: emailValidation,
      password: passwordValidation,
      addressLine1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);

      const selectedOfficeId = values.officeId;
      console.log("Selected Office ID:", selectedOfficeId);
      const selectedOffice = getOfficeById(selectedOfficeId);
      console.log("Selected Office:", selectedOffice);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: values.email,
              password: values.password,
              userType: "agent",
              firstName: values.firstName,
              lastName: values.lastName,
              phone1: values.phone1,
              phone2: values.phone2,
              licenseType: "BROKERAGE_AGENT",
              status: values.statuss,
              firmDetails: {
                companyID: "",
                companyName: "",
                companyWebsite: "",
                address: {
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  city: values.city,
                  state: values.state,
                  country: "US",
                  zip: values.zipcode,
                  phone: values.phone1,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: values.countryCode,
                  latitude: values.latitude,
                  longitude: values.longitude,
                },
              },
              agentDetails: {
                brokerageFirmAffiliation: brokerageFirm?.companyName,
                brokerageFirmID: brokerageFirm?.id,
                agentID: values.agentID,
                licenseNumber: values.licenseNumber,
                licenseExpiryDate: values.licenseExpiryDate,
              },
              offices: [{
                _id: selectedOffice?._id,
                officeName: selectedOffice?.officeName,
                officeType: selectedOffice?.officeType,
                officeAddress: {
                  addressLine1: selectedOffice?.officeAddress.addressLine1,
                  city: selectedOffice?.officeAddress.city,
                  state: selectedOffice?.officeAddress.state,
                  country: selectedOffice?.officeAddress.country,
                  zip: selectedOffice?.officeAddress.zip,
                  addressLine2: selectedOffice?.officeAddress.addressLine2 || "",
                  cityCode: "",
                  stateCode: "",
                  countryCode: "",
                  latitude: 0,
                  longitude: 0,
                }
              }],
              created_by: {
                id: firmId,
                name: "Brokerage Firm",
              },
            }),
          }
        );

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        console.log(result, "result");
        // navigate(`/signage/brokerageFirm/${firmId}#agents`);
        try {
          const addressResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/addresses/user/${result.id}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                user_id:`${result.id}`,
                label: `${values.firstName} ${values.lastName || ""} `,
                first_name: values.firstName,
                last_name: values.lastName,
                company: "",
                address_details: {
                  label: `${values.firstName} ${values.lastName || ""}`,
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  city: values.city,
                  state: values.state,
                  zip: values.zipcode,
                  country: "",
                  phone: values.phone1,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: values.countryCode,
                  latitude: values.latitude,
                  longitude: values.longitude,
                },
              }),
            }
          );

          if (!addressResponse.ok) {
            const error = await addressResponse.json();
            const message = error?.error || error?.message || "Failed to Add agent";
            console.error("API Error:", message);
            showError(message);
            throw new Error(message);

          }
        } catch (addressErr: any) {
          console.error(addressErr);
          showError(`Address Error: ${addressErr.message}`);
        }
        showSuccess("Agent added successfully!");;
        navigate(`/signage/brokerageFirm/${firmId}#agents`);
        // showSuccess("Agent Address added successfully!");;
      } catch (err) {
        console.error(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (!place || !place.address_components) {
        showError("Please select a valid address from the suggestions.");
        return;
      }
      if (!place || !place.address_components || !place.geometry) {
        console.warn("Google place is incomplete or undefined:", place);
        return;
      }

      const addressComponents = place.address_components;

      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };

        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;

        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');


        const street = `${streetNumber} ${route}`.trim();

        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();

        formik.setFieldValue('addressLine1', street);
        formik.setFieldValue('city', cityObj.long_name);
        formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        formik.setFieldValue('state', stateObj.long_name);
        formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        formik.setFieldValue('zipcode', postalCode);
        formik.setFieldValue('country', countryObj.long_name);
        formik.setFieldValue('countryCode', countryObj.short_name);

        if (latitude && longitude) {
          formik.setFieldValue('latitude', latitude);
          formik.setFieldValue('longitude', longitude);
        }
        // :white_tick: ZIP code logic with fallback
        if (postalCode) {
          formik.setFieldValue("zipcode", postalCode);
        } else if (latitude && longitude) {
          // Fallback to reverse geocoding
          fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          )
            .then((res) => res.json())
            .then((data) => {
              const postalComp = data.results[0]?.address_components.find((c: any) =>
                c.types.includes("postal_code")
              );
              if (postalComp) {
                console.log("Postal Code from Reverse Geocoding:", postalComp.long_name);
                formik.setFieldValue("zipcode", postalComp.long_name);
              } else {
                console.warn("No postal_code found even from reverse geocoding");
              }
            })
            .catch((err) => console.error("Reverse geocoding error:", err));
        }
      }
    }
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('cityCode', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('stateCode', '', false);
    formik.setFieldValue('zipcode', '', false);
    formik.setFieldValue('country', '', false);
    formik.setFieldValue('countryCode', '', false);
    formik.setFieldValue('latitude', 0, false);
    formik.setFieldValue('longitude', 0, false);
  };

  const handleAddressLine1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    if (!e.target.value.trim()) {
      clearGoogleAddressFields();
    }
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content">
      <div className="page-container mt-3 col-md-9">
        <h3 className="m-0 text-center fw-bold addheaders">Add Brokerage Agent</h3>
      </div>
      {/* <h2 className="d-flex justify-content-center">Add Office </h2> */}
      <div className="row ms-0">
        <div className="col-md-12" id="row-overlap">
          <div className="col-md-9">
            <div className="addheaders-content mt-1 card-bg-fill">
              <div className="p-2 px-4 mt-1">
                <form onSubmit={formik.handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}

                      />
                      {formik.touched.firstName &&
                        formik.errors.firstName ? (
                        <div className="text-danger">
                          {formik.errors.firstName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}

                      />
                      {formik.touched.lastName && formik.errors.lastName ? (
                        <div className="text-danger">
                          {formik.errors.lastName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Phone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone1"
                        name="phone1"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone1}

                      />
                      {formik.touched.phone1 && formik.errors.phone1 ? (
                        <div className="text-danger">
                          {formik.errors.phone1}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Secondary Phone Number{" "}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone2"
                        name="phone2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone2}
                      />
                      {formik.touched.phone2 && formik.errors.phone2 ? (
                        <div className="text-danger">
                          {formik.errors.phone2}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}

                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-danger">
                          {formik.errors.email}
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative auth-pass-inputgroup">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          className="form-control pe-5 password-input"
                          id="password"
                          name="password"
                          onChange={handlePasswordChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          value={formik.values.password}
                          aria-describedby="password"
                        />
                        <button
                          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon material-shadow-none"
                          type="button"
                          id="password-addon"
                          onClick={togglePasswordVisibility}
                        >
                          <i
                            className={
                              passwordVisible
                                ? "ri-eye-off-fill align-middle"
                                : "ri-eye-fill align-middle"
                            }
                          ></i>
                        </button>
                      </div>

                      {formik.touched.password && formik.errors.password && (
                        <div className="text-danger mt-1">{formik.errors.password}</div>
                      )}

                      {showValidationBox && (
                        <div className="bg-light border rounded p-2 mt-2 shadow-sm">
                          <small className="fw-semibold">Password must contain:</small>
                          <ul className="list-unstyled mb-0 mt-2">
                            <li>
                              <i
                                className={
                                  validation.length
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least 8 characters</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.uppercase
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least one uppercase letter</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.lowercase
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least one lowercase letter</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.digit
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">At least one digit</span>
                            </li>
                            <li>
                              <i
                                className={
                                  validation.specialChar
                                    ? "ri-check-line text-success"
                                    : "ri-close-line text-danger"
                                }
                              ></i>
                              <span className="ms-2">
                                At least one special character (e.g., !@#$%^&*)
                              </span>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <hr />
                  <h4>Professional Details </h4>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Brokerage Firm Affiliation{" "}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="brokerageFirmAffiliation"
                        name="brokerageFirmAffiliation"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.brokerageFirmAffiliation}
                        aria-describedby="brokerageFirmAffiliation"
                        placeholder="Enter Brokerage Firm Affiliation"
                      />
                      {formik.touched.brokerageFirmAffiliation && formik.errors.brokerageFirmAffiliation ? (
                        <div className="text-danger">
                          {formik.errors.brokerageFirmAffiliation}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Office</label>
                      <select
                        className="form-control br-8"
                        id="officeId"
                        name="officeId"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.officeId}
                      >
                        <option value="">- Select -</option>
                        {offices !== null &&
                          offices?.map((office) => (
                            <option key={office._id} value={office._id}>
                              {office.officeName}
                            </option>
                          ))}
                      </select>
                      {formik.touched.officeId && formik.errors.officeId ? (
                        <div className="text-danger">
                          {formik.errors.officeId}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Agent ID/Code </label>
                      <input
                        type="text"
                        className="form-control"
                        id="agentID"
                        name="agentID"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.agentID}
                        aria-describedby="agentID"
                        placeholder="Enter Agent ID/Code"
                      />
                      {formik.touched.agentID && formik.errors.agentID ? (
                        <div className="text-danger">
                          {formik.errors.agentID}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">License Number </label>
                      <input
                        type="text"
                        className="form-control"
                        id="licenseNumber"
                        name="licenseNumber"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.licenseNumber}
                        aria-describedby="licenseNumber"
                        placeholder="Enter license number"
                      />
                      {formik.touched.licenseNumber && formik.errors.licenseNumber ? (
                        <div className="text-danger">
                          {formik.errors.licenseNumber}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">License Expiry Date </label>
                      <input
                        type="date"
                        className="form-control"
                        id="licenseExpiryDate"
                        name="licenseExpiryDate"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.licenseExpiryDate}
                        aria-describedby="licenseExpiryDate"
                        placeholder="Enter license expiry date"
                      />
                      {formik.touched.licenseExpiryDate && formik.errors.licenseExpiryDate ? (
                        <div className="text-danger">
                          {formik.errors.licenseExpiryDate}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Street Name <span className="text-danger">*</span>
                      </label>
                      {
                        isLoaded &&
                        <Autocomplete
                          onLoad={(autocomplete) =>
                            (autocompleteRef.current = autocomplete)
                          }
                          onPlaceChanged={handlePlaceChanged}
                        >
                          <input
                            type="text"
                            className="form-control"
                            id="addressLine1"
                            name="addressLine1"
                            onChange={handleAddressLine1Change}
                            onBlur={formik.handleBlur}
                            value={formik.values.addressLine1}

                          />
                        </Autocomplete>
                      }
                      {formik.touched.addressLine1 && formik.errors.addressLine1 ? (
                        <div className="text-danger">
                          {formik.errors.addressLine1}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Address line 2 </label>
                      <input
                        type="text"
                        className="form-control"
                        id="addressLine2"
                        name="addressLine2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.addressLine2}
                      />
                      {formik.touched.addressLine2 && formik.errors.addressLine2 ? (
                        <div className="text-danger">
                          {formik.errors.addressLine2}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.city}
                        disabled
                      />
                      {formik.touched.city && formik.errors.city ? (
                        <div className="text-danger">
                          {formik.errors.city}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className="form-control"
                        id="state"
                        name="state"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.state}
                        disabled
                      />
                      {formik.touched.state && formik.errors.state ? (
                        <div className="text-danger">
                          {formik.errors.state}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Zip code</label>
                      <input
                        type="text"
                        className="form-control"
                        id="zipcode"
                        name="zipcode"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.zipcode}
                        disabled
                      />
                      {formik.touched.zipcode && formik.errors.zipcode ? (
                        <div className="text-danger">
                          {formik.errors.zipcode}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="statuss"
                        name="statuss"

                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.statuss}
                      >
                        <option value="">- Select -</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {formik.touched.statuss && formik.errors.statuss ? (
                        <div className="text-danger">
                          {formik.errors.statuss}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="row mb-3 rcust-margin-4">
                    <div className="col-md-12 d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary br-8 backbutton"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>

                      <button className="btn btn-primary submit-btn" type="submit">
                        Submit
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageBrokerageAddAgent;
